import React from 'react';
import { Card } from '@blueprintjs/core';
import { IconSize, ObjectIcon } from './ObjectIcon';
import SparklineChart from './SparklineChart';
import { EndpointDisplayInfo } from '../utils/endpointDisplayInfo';
import { formatSignedInteger, formatSignedPercent, PeriodChange } from '../utils/analyticsMath';
import styles from '../DataSyncAnalyticsPage.module.sass';

interface AnalyticsOverviewCardsProps {
  selectedStageKey: string;
  sourceDisplayInfo: EndpointDisplayInfo;
  destinationDisplayInfo: EndpointDisplayInfo;
  totalReceived: number;
  totalDelivered: number;
  receivedTrend: PeriodChange;
  deliveredTrend: PeriodChange;
  receivedSeries: number[];
  deliveredSeries: number[];
  formatInteger: (value: number) => string;
  onSelectStage: (key: 'received' | 'delivered') => void;
}

const onOverviewCardKeyDown = (
  event: React.KeyboardEvent<HTMLDivElement>,
  onSelect: () => void
) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    onSelect();
  }
};

const AnalyticsOverviewCards: React.FC<AnalyticsOverviewCardsProps> = ({
  selectedStageKey,
  sourceDisplayInfo,
  destinationDisplayInfo,
  totalReceived,
  totalDelivered,
  receivedTrend,
  deliveredTrend,
  receivedSeries,
  deliveredSeries,
  formatInteger: format,
  onSelectStage,
}) => (
  <div className={styles.overviewGrid}>
    <Card
      className={`${styles.largeCard} ${selectedStageKey === 'received' ? styles.activeCard : ''}`}
      onClick={() => onSelectStage('received')}
      role="button"
      tabIndex={0}
      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
        onOverviewCardKeyDown(event, () => onSelectStage('received'));
      }}
    >
      <div className={styles.cardHeader}>
        <div className={styles.cardLabel}>Events received</div>
        <div className={styles.cardContextPill}>
          <ObjectIcon
            size={IconSize.SMALL}
            src={sourceDisplayInfo.iconPath}
            alt={sourceDisplayInfo.productName}
          />
          <span className={styles.cardContextProduct}>{sourceDisplayInfo.productName}</span>
        </div>
      </div>
      <div className={styles.cardValue}>{format(totalReceived)} received</div>
      <SparklineChart
        data={receivedSeries}
        width="100%"
        height={66}
        lineColor={styles.overviewSparklineLine}
        fillColor={styles.overviewSparklineFill}
      />
      <div className={styles.trendLine}>
        Change from previous period: {formatSignedInteger(receivedTrend.delta)}
        {' '}
        <span className={receivedTrend.delta < 0 ? styles.trendNegative : styles.trendPositive}>
          ({formatSignedPercent(receivedTrend.deltaPercent)})
        </span>
      </div>
    </Card>

    <Card
      className={`${styles.largeCard} ${selectedStageKey === 'delivered' ? styles.activeCard : ''}`}
      onClick={() => onSelectStage('delivered')}
      role="button"
      tabIndex={0}
      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
        onOverviewCardKeyDown(event, () => onSelectStage('delivered'));
      }}
    >
      <div className={styles.cardHeader}>
        <div className={styles.cardLabel}>Events delivered</div>
        <div className={styles.cardContextPill}>
          <ObjectIcon
            size={IconSize.SMALL}
            src={destinationDisplayInfo.iconPath}
            alt={destinationDisplayInfo.productName}
          />
          <span className={styles.cardContextProduct}>{destinationDisplayInfo.productName}</span>
        </div>
      </div>
      <div className={styles.cardValue}>{format(totalDelivered)} delivered</div>
      <SparklineChart
        data={deliveredSeries}
        width="100%"
        height={66}
        lineColor={styles.overviewSparklineLine}
        fillColor={styles.overviewSparklineFill}
      />
      <div className={styles.trendLine}>
        Change from previous period: {formatSignedInteger(deliveredTrend.delta)}
        {' '}
        <span className={deliveredTrend.delta < 0 ? styles.trendNegative : styles.trendPositive}>
          ({formatSignedPercent(deliveredTrend.deltaPercent)})
        </span>
      </div>
    </Card>
  </div>
);

export default AnalyticsOverviewCards;
